import {
  OrderWithOrderCustomersDTOStatusIdEnum,
  PaymentPlanDTOStatusEnum,
  PaymentPlanInstalmentDTOStatusEnum,
} from '@reposit/api-client';

export type DashboardPaymentPlan = {
  id: string;
  status: PaymentPlanDTOStatusEnum;
  orderCustomer?: {
    customer?: {
      id?: string;
    };
  };
  instalments?: Array<{
    status: PaymentPlanInstalmentDTOStatusEnum;
  }>;
};

export type DashboardCustomer = {
  hasDuplicatePaymentMethod: boolean;
  requiresNewPaymentMethod: boolean;
};

export type DashboardUnpaidTopUp = {
  order?: {
    id: string;
  };
};

export type OutstandingRepositTenancyOrder = {
  order: {
    id: string;
    statusId: OrderWithOrderCustomersDTOStatusIdEnum;
    orderCustomers: Array<{
      customer: {
        id: string;
      };
      nextAction?: {
        orderCustomerActionIds: string[];
      } | null;
    }>;
  };
};

export interface DashboardRedirectInput {
  hasLoadedDashboardData: boolean;
  paymentPlan: DashboardPaymentPlan | undefined;
  hasPaymentPlan: boolean;
  claimId: string | null;
  claimAutoChargeEnabled?: boolean | null;
  arbitration: { id: string } | null;
  hasOutstandingRepositActions: boolean;
  repositActionOrderId: string | null;
  customerId: string;
  activeUnpaidTopUp: DashboardUnpaidTopUp | undefined;
  customer: DashboardCustomer | undefined;
}

export type DashboardRedirect =
  | { to: string }
  | { waitForDashboardData: true }
  | { waitForCustomer: true }
  | null;

/**
 * Pure function that encodes the Dashboard redirect priority order.
 *
 * Priority (highest to lowest):
 * 1. Arbitration — skipped when an active PP is paying off those charges, or
 *    when a new Reposit checkout should take precedence over an older arbitration
 * 2. Failed payment plan instalment
 * 3. Active claim (skipped when an active PP is paying off those charges, or
 *    when a new Reposit checkout should take precedence over an older claim)
 * 4. Pending payment plan setup
 * 5. Outstanding Reposit actions (skipped when active PP)
 * 6. Unpaid annual fee top-up
 * 7. Active payment plan with no outstanding actions
 * 8. Duplicate/expired payment method
 *
 * Returns `{ to: string }` for a redirect, a wait object when required data is
 * not yet loaded, or `null` when no redirect applies.
 */
export function getDashboardRedirect(input: DashboardRedirectInput): DashboardRedirect {
  const {
    hasLoadedDashboardData,
    paymentPlan,
    hasPaymentPlan,
    claimId,
    claimAutoChargeEnabled,
    arbitration,
    hasOutstandingRepositActions,
    repositActionOrderId,
    customerId,
    activeUnpaidTopUp,
    customer,
  } = input;

  const isCurrentCustomersPaymentPlan = paymentPlan?.orderCustomer?.customer?.id === customerId;

  const isPPActive = !!(hasPaymentPlan && paymentPlan && paymentPlan.status === PaymentPlanDTOStatusEnum.ACTIVE);
  const isPPPending = !!(isCurrentCustomersPaymentPlan && paymentPlan && paymentPlan.status === PaymentPlanDTOStatusEnum.PENDING);
  const hasPPFailedInstalment = !!(
    isPPActive &&
    isCurrentCustomersPaymentPlan &&
    paymentPlan &&
    paymentPlan.instalments?.some((i) => i.status === PaymentPlanInstalmentDTOStatusEnum.FAILED)
  );
  const shouldSkipChargeRedirect =
    claimAutoChargeEnabled === false && hasOutstandingRepositActions;

  if (!hasLoadedDashboardData) {
    return { waitForDashboardData: true };
  }

  // Arbitration takes priority unless an active PP is covering it, or a new
  // Reposit checkout should take precedence while auto-charge is off.
  if (arbitration && !isPPActive && !shouldSkipChargeRedirect) {
    return { to: `/arbitrations/${arbitration.id}` };
  }

  if (hasPPFailedInstalment) {
    return { to: `/payment-plan/${paymentPlan!.id}` };
  }

  if (claimId && !isPPActive && !shouldSkipChargeRedirect) {
    return { to: `/claims/${claimId}` };
  }

  if (isPPPending) {
    return { to: `/payment-plan/${paymentPlan!.id}` };
  }

  if (hasOutstandingRepositActions && repositActionOrderId && !isPPActive) {
    if (!customerId) return { waitForCustomer: true };
    return { to: `/checkout/${repositActionOrderId}/${customerId}` };
  }

  if (activeUnpaidTopUp && activeUnpaidTopUp.order) {
    if (!customerId) return { waitForCustomer: true };
    return { to: `/annual-fee/${activeUnpaidTopUp.order.id}/${customerId}` };
  }

  if (isPPActive && !hasOutstandingRepositActions) {
    return { to: `/payment-plan/${paymentPlan!.id}` };
  }

  if (customer && (customer.hasDuplicatePaymentMethod || customer.requiresNewPaymentMethod)) {
    return { to: `/add-payment-method` };
  }

  return null;
}

/**
 * Finds the first tenancy order that has a pending next action for the given
 * customer, returning both a boolean flag and the order ID so callers can
 * build a direct checkout URL.
 */
export function getOutstandingRepositAction(
  tenancyOrders: OutstandingRepositTenancyOrder[],
  customerId: string,
): { hasOutstandingRepositActions: boolean; repositActionOrderId: string | null } {
  const tenancyOrderWithAction = tenancyOrders.find((tenancyOrder) => {
    const { order } = tenancyOrder;

    if (order.statusId === OrderWithOrderCustomersDTOStatusIdEnum.COMPLETE) return false;

    const currentOrderCustomer = order.orderCustomers.find((oc) => oc.customer.id === customerId);

    return currentOrderCustomer ? !!currentOrderCustomer.nextAction : false;
  });

  return {
    hasOutstandingRepositActions: !!tenancyOrderWithAction,
    repositActionOrderId: tenancyOrderWithAction ? tenancyOrderWithAction.order.id : null,
  };
}
